export interface ReviewTransportRequest {
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  url: string
  params?: Record<string, string | number | boolean | null | undefined>
  body?: unknown
  headers?: Record<string, string>
}

export interface ReviewTransport {
  request<T>(request: ReviewTransportRequest): Promise<T>
}

export interface FetchReviewTransportOptions {
  baseUrl: string
  getAccessToken?: () => string | null | undefined
  onUnauthorized?: () => void
}

function buildUrl(baseUrl: string, url: string, params?: ReviewTransportRequest['params']) {
  const normalizedBase = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`
  const normalizedUrl = url.startsWith('/') ? url.slice(1) : url
  const finalUrl = new URL(normalizedUrl, normalizedBase)

  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value === null || value === undefined || value === '') continue
      finalUrl.searchParams.set(key, String(value))
    }
  }

  return finalUrl.toString()
}

export class ReviewApiError extends Error {
  status: number | undefined
  payload: unknown | undefined

  constructor(message: string, options?: { status?: number; payload?: unknown }) {
    super(message)
    this.name = 'ReviewApiError'
    this.status = options?.status
    this.payload = options?.payload
  }
}

export function createFetchReviewTransport(
  options: FetchReviewTransportOptions
): ReviewTransport {
  return {
    async request<T>(request: ReviewTransportRequest) {
      const headers: Record<string, string> = {
        Accept: 'application/json',
        ...request.headers
      }

      if (request.body !== undefined) {
        headers['Content-Type'] = headers['Content-Type'] ?? 'application/json'
      }

      const accessToken = options.getAccessToken?.()
      if (accessToken) {
        headers.Authorization = `Bearer ${accessToken}`
      }

      const init: RequestInit = {
        method: request.method,
        headers
      }

      if (request.body !== undefined) {
        init.body = headers['Content-Type']?.includes('application/json')
          ? JSON.stringify(request.body)
          : (request.body as BodyInit)
      }

      const response = await fetch(buildUrl(options.baseUrl, request.url, request.params), init)

      const contentType = response.headers.get('content-type') || ''
      const payload =
        contentType.includes('application/json') ? await response.json() : await response.text()

      if (!response.ok) {
        if (response.status === 401) {
          options.onUnauthorized?.()
        }

        const message =
          (payload && typeof payload === 'object' && 'message' in payload
            ? String((payload as { message?: string }).message)
            : response.statusText) || 'Request failed'

        throw new ReviewApiError(message, {
          status: response.status,
          payload
        })
      }

      return payload as T
    }
  }
}
